/**
 * «Перелік СЕС» (/assets) — РЕДИЗАЙН (VITE_REDESIGN).
 *
 * Прод без прапорця бачить незмінений AssetsPageLegacy у page.tsx: маршрут не
 * гейтований жодним VITE_FEATURE_*, тож у прод-збірці ця сторінка є.
 *
 * Що виправлено проти старої версії:
 *  · власна палітра (#1A1A1A / #E0E0E0 / #6B6B70 / #F5F5F7 і статуси
 *    #0D7A3C / #B8860B / #1F5B8F / var(--accent)) — на токени;
 *  · іконка типу в кожному рядку була яскраво-червоною (var(--accent)) — це декор,
 *    а §3 лишає брендовий червоний за реальною помилкою;
 *  · явний Inter на h1 і font-mono на EIC — §0 забороняє обидва в новому коді;
 *  · англійські підписи типів і статусів в україномовному інтерфейсі — тепер
 *    через i18n (asset_type_* / asset_status_*), регіон — через localizeRegion;
 *  · заголовок вирівняно з пунктом меню: «Перелік СЕС», а не «Активи»;
 *  · пошук і фільтр переїхали в топбар (§6, useAssetsUi).
 *
 * Доступність: стрілка в кінці рядка більше не фокусована кнопка-пустушка, а
 * рядки отримали tabIndex і Enter/Space — раніше в картку станції не було як
 * потрапити з клавіатури.
 *
 * Запит, обробка 404, тости й клієнтська фільтрація — той самий код.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useAssetsUi } from '@/hooks/useAssetsUi';
import { localizeRegion } from '@/utils/region';
import { formatEic } from '@/utils/eic';
import {
  Battery, Building2, ChevronRight, RefreshCw, Sun, Wind, Zap, type LucideIcon,
} from 'lucide-react';

interface Asset {
  id: string;
  assetName: string;
  assetType: string;
  eicCode: string;
  eicWCode?: string | null;
  installedCapacityMw: number;
  region: string;
  operationalStatus: string;
  counterpartyName: string;
  commissioningDate: string;
}

const TYPE_ICON: Record<string, LucideIcon> = {
  solar: Sun,
  wind: Wind,
  bess: Battery,
  hybrid: Zap,
  consumer_site: Building2,
};

/**
 * Статуси за догмою §3: працює → --ok; будівництво, пусконалагодження та
 * призупинення → --warn (увага, не збій); заплановано й виведено → приглушений.
 * Червоного тут немає: зупинена станція — не поломка системи.
 */
const STATUS_COLOR: Record<string, string> = {
  active: 'var(--ok)',
  construction: 'var(--warn)',
  commissioning: 'var(--warn)',
  suspended: 'var(--warn)',
  planned: 'var(--text-dim)',
  decommissioned: 'var(--text-dim)',
};

export default function AssetsRedesign() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const { search, type: typeFilter } = useAssetsUi();

  const [items, setItems] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const loadAssets = async () => {
    setLoading(true);
    try {
      const data = await api.get<Asset[]>('/assets');
      setItems(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setItems([]);
      } else {
        const message = getApiErrorMessage(err, 'Failed to load assets');
        addToast(message, 'error');
      }
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => { loadAssets(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => items.filter((a) => {
    const q = search.toLowerCase();
    const matchesSearch = a.assetName.toLowerCase().includes(q)
      || a.eicCode.includes(search) || (a.eicWCode || '').includes(search);
    const matchesType = !typeFilter || a.assetType === typeFilter;
    return matchesSearch && matchesType;
  }), [items, search, typeFilter]);

  const typeLabel = (v: string) => t(`asset_type_${v}`, { defaultValue: v.replace('_', ' ') });
  const statusLabel = (v: string) => t(`asset_status_${v}`, { defaultValue: v.replace('_', ' ') });

  const th: any = {
    textAlign: 'left', padding: '10px 14px', fontSize: 10.5, fontWeight: 500,
    letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)',
    background: 'var(--surface-2)', borderBottom: '1px solid var(--line)', whiteSpace: 'nowrap',
  };
  const td: any = {
    padding: '10px 14px', fontSize: 12.5, color: 'var(--text)',
    borderBottom: '1px solid var(--line)',
  };

  const statusPill = (s: string) => {
    const c = STATUS_COLOR[s] ?? 'var(--text-dim)';
    return (
      <span className="text-[10.5px] font-semibold px-2 py-0.5 whitespace-nowrap"
        style={{ borderRadius: 999, color: c, border: `1px solid ${c}`,
                 background: `color-mix(in srgb, ${c} 12%, transparent)` }}>
        {statusLabel(s)}
      </span>
    );
  };

  const firstLoad = loading && !loaded;
  const open = (id: string) => navigate(`/assets/${id}`);

  return (
    <DashboardLayout breadcrumb={[t('nav_assets_list')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className="px-4 md:px-6 pt-5 pb-10" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          {/* Заголовок у тілі не дублюємо: назву розділу показує топбар.
              Лишається лише функціональна кнопка оновлення. */}
          <div className="mb-3 flex justify-end">
            <button onClick={loadAssets} disabled={loading} title={t('assets_loading')}
              className="inline-flex items-center justify-center cursor-pointer shrink-0"
              style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--surface-2)',
                       border: '1px solid var(--line)', color: 'var(--text-muted)',
                       opacity: loading ? 0.55 : 1 }}>
              <RefreshCw size={15} strokeWidth={2} className={loading ? 'animate-spin' : undefined} />
            </button>
          </div>

          <div className="tile overflow-hidden">
            {firstLoad ? (
              <div className="p-12 text-center">
                <RefreshCw size={22} strokeWidth={2} className="animate-spin mx-auto" style={{ color: 'var(--text-muted)' }} />
                <p className="text-[12.5px] mt-3" style={{ color: 'var(--text-muted)' }}>{t('assets_loading')}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <Zap size={22} strokeWidth={1.8} className="mx-auto mb-3" style={{ color: 'var(--text-dim)' }} />
                <p className="text-[13px] font-medium" style={{ color: 'var(--text)' }}>{t('assets_not_found')}</p>
                <p className="text-[11.5px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  {search || typeFilter ? t('assets_filter_hint') : t('assets_not_found_hint')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>{t('assets_col_asset')}</th>
                      <th style={th}>{t('assets_col_eic')}</th>
                      <th style={th}>{t('assets_col_type')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('assets_col_capacity')}</th>
                      <th style={th}>{t('assets_col_region')}</th>
                      <th style={th}>{t('assets_col_status')}</th>
                      <th style={th}>{t('assets_col_counterparty')}</th>
                      <th style={{ ...th, width: 44 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((asset) => {
                      const Icon = TYPE_ICON[asset.assetType] ?? Zap;
                      return (
                        <tr key={asset.id} tabIndex={0} role="link"
                          aria-label={`${asset.assetName} — ${t('assets_col_asset')}`}
                          onClick={() => open(asset.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(asset.id); }
                          }}
                          className="cursor-pointer transition-colors outline-none"
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          onFocus={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
                          onBlur={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                          <td style={td}>
                            <div className="flex items-center gap-2.5">
                              {/* Іконка типу — нейтральна: колір тут нічого не означає. */}
                              <Icon size={16} strokeWidth={1.9} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                              <span className="font-medium">{asset.assetName}</span>
                            </div>
                          </td>
                          <td style={{ ...td, color: 'var(--text-muted)' }}>
                            <div className="text-[11.5px]">{asset.eicCode}</div>
                            {/* Тип беремо з коду, а не зашиваємо: у портфелі є і W, і Z. */}
                            {asset.eicWCode && (
                              <div className="text-[10px]" style={{ color: 'var(--text-dim)' }}>{formatEic(asset.eicWCode)}</div>
                            )}
                          </td>
                          <td style={{ ...td, color: 'var(--text-muted)' }}>{typeLabel(asset.assetType)}</td>
                          <td className="font-display" style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {asset.installedCapacityMw}
                            <span className="text-[9.5px] ml-1" style={{ color: 'var(--text-muted)' }}>МВт</span>
                          </td>
                          <td style={{ ...td, color: 'var(--text-muted)' }}>
                            {localizeRegion(asset.region, i18n.language)}
                          </td>
                          <td style={td}>{statusPill(asset.operationalStatus)}</td>
                          <td style={{ ...td, color: 'var(--text-muted)', fontSize: 11.5 }}>{asset.counterpartyName}</td>
                          <td style={{ ...td, textAlign: 'right' }}>
                            {/* Раніше тут стояв <button> без onClick: у фокус потрапляв,
                                але нічого не робив. Тепер це просто вказівник. */}
                            <ChevronRight size={16} strokeWidth={2} aria-hidden="true"
                              style={{ color: 'var(--text-dim)' }} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!firstLoad && filtered.length > 0 && (
            <div className="text-[11px] mt-2" style={{ color: 'var(--text-dim)' }}>
              Показано {filtered.length} з {items.length} · рядок відкриває картку станції
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
